import type {
  AssignUserToProjectRequest,
  AssignUserToProjectResponse,
  CreateUserRequest,
  GetProjectAssignmentsParams,
  GetUsersParams,
  PaginatedProjectAssignmentsResponse,
  PaginatedUsersResponse,
  Role,
  User,
  UserConfig,
} from '@/interfaces/admin.interface';
import api from './base.api';

const USERS_BASE_PATH = '/users';

export const getUsers = async (params?: GetUsersParams) => {
  const response = await api.get<PaginatedUsersResponse>(USERS_BASE_PATH, {
    params,
  });
  return response.data;
};

export const createUser = async (userData: CreateUserRequest) => {
  const response = await api.post<User>(USERS_BASE_PATH, userData);
  return response.data;
};

/** The signed-in user, with their settings. */
export const getCurrentUser = async () => {
  const response = await api.get<User>(`${USERS_BASE_PATH}/me`);
  return response.data;
};

/** Changes only the keys given; a key given as null is cleared. */
export const updateCurrentUserConfig = async (config: UserConfig) => {
  const response = await api.patch<User>(`${USERS_BASE_PATH}/me/config`, config);
  return response.data;
};

/** An administrator changing another user's settings. */
export const updateUserConfig = async (userId: string, config: UserConfig) => {
  const response = await api.patch<User>(`${USERS_BASE_PATH}/${userId}/config`, config);
  return response.data;
};

export const deleteUser = async (userId: string) => {
  const response = await api.delete<void>(`${USERS_BASE_PATH}/${userId}`);
  return response.data;
};

export const assignUserToProject = async (
  userId: string,
  request: AssignUserToProjectRequest
) => {
  const response = await api.post<AssignUserToProjectResponse>(
    `${USERS_BASE_PATH}/${userId}/assign-to-project`,
    request
  );
  return response.data;
};

export const unassignUserFromProject = async (
  userId: string,
  projectId: string
) => {
  const response = await api.delete<void>(
    `${USERS_BASE_PATH}/${userId}/unassign-from-project/${projectId}`
  );
  return response.data;
};

export const assignProjectsToUser = async (
  userId: string,
  projectIds: string[],
  strictAccessControl: boolean = false
) => {
  const assignments = await Promise.all(
    projectIds.map((projectId) =>
      assignUserToProject(userId, { projectId, strictAccessControl })
    )
  );
  return assignments;
};

export const assignRoleToUser = async (userId: string, role: string) => {
  const response = await api.post<void>(`${USERS_BASE_PATH}/${userId}/roles/assign`, role, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response.data;
};

export const unassignRoleFromUser = async (userId: string, role: string) => {
  const response = await api.put<void>(
    `${USERS_BASE_PATH}/${userId}/roles/unassign`,
    role,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
  return response.data;
};

export const getUserRoles = async (userId: string) => {
  const response = await api.get<Role[]>(`${USERS_BASE_PATH}/${userId}/roles`);
  return response.data;
};

export const getProjectAssignments = async (
  params?: GetProjectAssignmentsParams
) => {
  const response = await api.get<PaginatedProjectAssignmentsResponse>(
    `${USERS_BASE_PATH}/project-assignments`,
    {
      params,
    }
  );
  return response.data;
};
